<template>
  <div class="p-3 w-full flex flex-col gap-2 m m-auto h-[700px]">
    <Toolbar>
      <template #start>
        <div class="flex gap-1">
          <IconButton v-tooltip="'Select all unprocessed payments'" icon="list-check" outlined @click="selectAllUnprocessedRows"></IconButton>
          <IconButton v-tooltip="'Clear all selected payments'" icon="eraser" outlined @click="clearSelectedRows"></IconButton>
          <IconButton v-tooltip="'Process selected payments'" icon="check" outlined @click="askProcessPayments"></IconButton>
        </div>
      </template>
      <template #center>
        <div v-if="!props.project" class="flex flex-col gap-1">
          <label class="font-bold text-surface-800 dark:text-surface-200">Days to fetch:</label>
          <InputGroup>
            <InputNumber v-model="daysToFetch" class="h-10" input-class="rounded-l-lg!" v-on:keyup.enter="reloadPayments"></InputNumber>
            <RefreshButton @refresh="reloadPayments"></RefreshButton>
          </InputGroup>
        </div>
      </template>
      <template #end>
        <div v-if="!props.project" class="flex gap-1">
          <IconButton v-tooltip.left="'Combine Fed Wires'" icon="receipt" outlined></IconButton>
          <IconButton
            v-tooltip.left="'Export previously unexported line items to file'"
            icon="file-export"
            outlined
            @click="exportLedger"
          ></IconButton>
          <IconButton v-tooltip.left="'Import Interest Reserve from file'" icon="file-import" outlined @click="openImportInterestModal"></IconButton>
          <IconButton v-tooltip.left="'Reconcile project available funds'" icon="calculator" outlined></IconButton>
        </div>
      </template>
    </Toolbar>
    <div class="w-full flex-auto">
      <ag-grid-vue
        theme="legacy"
        :get-row-id="getRowId"
        :rowData="payments"
        :columnDefs="columns"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitGridWidth' }"
        :class="(darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz') + ' py-3 h-full'"
        :row-selection="'multiple'"
        :suppress-row-click-selection="true"
        :stopEditingWhenCellsLoseFocus="true"
        :singleClickEdit="true"
        :rowClassRules="rowClassRules"
        :isRowSelectable="isRowSelectable"
        @cell-clicked="onCellClicked"
        @cell-editing-stopped="updateNotes"
        @row-data-updated="resize"
        @grid-size-changed="resize"
        @selection-changed="updateSelected"
        @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
      >
      </ag-grid-vue>
    </div>
  </div>
  <TSDialog label="Import Interest Reserve" v-model:visible="showImportInterest" icon="plus" size="w-[400px]!">
    <template #content>
      <div class="flex flex-col gap-5">
        <FileUploadButton v-model:file="interestImportFile" size="w-full!" class="mb-2" accept=".csv"></FileUploadButton>
        <p v-if="interestImported" class="text-center whitespace-pre-wrap" :class="interestImportHasErrors ? 'text-danger-500' : 'text-success-500'">
          {{ interestImportHasErrors ? "Import Completed With Exceptions" : "Import Completed Without Exceptions" }}
          {{ `\n${interestLinesSkipped} Previously Successful Line(s) Skipped` }}
        </p>
        <div class="flex flex-row w-full justify-end gap-2">
          <Button
            label="View Report"
            :icon="launching ? 'pi pi-spin pi-spinner' : ''"
            iconPos="right"
            class="h-10"
            :disabled="!interestImported"
            @click="downloadImportReport"
          ></Button>
          <SubmitButton label="Import" :show-icon="false" @click="importInterest"></SubmitButton>
        </div>
      </div>
    </template>
  </TSDialog>
  <TSDialog label="Reconcile Projects" v-model:visible="showProjectReconcile" icon="calculator" size="w-[400px]!">
    <template #content>
      <div class="flex flex-col gap-5">
        <FileUploadButton v-model:file="projectReconcileFile" size="w-full!" class="mb-2" accept=".txt" label="Reconcile File"></FileUploadButton>
        <p
          v-if="projectReconcileResults"
          class="text-center whitespace-pre-wrap"
          :class="projectReconcileResults.hasErrors ? 'text-danger-500' : 'text-success-500'"
        >
          {{ projectReconcileResults.hasErrors ? "Reconcile Completed With Exceptions" : "Reconcile Completed Without Exceptions" }}
          {{ `\n${projectReconcileResults.linesSkipped} Previously Successful Line(s) Skipped` }}
        </p>
        <div class="flex flex-row w-full justify-end gap-2">
          <Button
            label="View Report"
            :icon="launching ? 'pi pi-spin pi-spinner' : ''"
            iconPos="right"
            class="h-10"
            :disabled="!projectReconcileResults"
            @click="downloadProjectReconcileReport"
          ></Button>
          <SubmitButton label="Reconcile" :show-icon="false" @click="reconcileProjects"></SubmitButton>
        </div>
      </div>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { usePropsStore } from "@/stores/props";
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ProjectEntityListSchema, type ProjectEntity } from "@/types/ProjectEntity";
import type { Project } from "@/types/Projects";
import { CheckLedgerListSchema, type CheckLedger } from "@/types/Ledger";
import { buildUrl, fileSaveAs, formatCurrency } from "@/services/Utility";
import type { CheckingAccount } from "@/types/CheckingAccount";
import type { UserPermissions } from "@/types/User";
import { permissionError } from "@/services/Permissions";
import { useConfirm } from "primevue/useconfirm";
import {
  InterestReserveImportResponseSchema,
  type ProjectReconcileResponse,
  ProjectReconcileResponseSchema,
  type InterestReserveImport,
} from "@/types/Ledger";
import type { ReportOptions } from "@/types/Reports";
import CustomTagRenderer from "../cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import ErrorIconCellRenderer from "../cell-renderers/ErrorIconCellRenderer.vue";
import type {
  CellClickedEvent,
  CellEditingStoppedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  RowClassParams,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";

interface Props {
  project?: Project;
  permissions: UserPermissions;
  showTotals?: boolean;
}

const ledger = defineModel<CheckingAccount>("ledger", { required: true });

const props = withDefaults(defineProps<Props>(), {
  showTotals: false,
});

const route = useRoute();
const gridApi = ref<GridApi>();
const confirm = useConfirm();

const toProcessCount = ref<number>(0);
const toVoidCount = ref<number>(0);

const daysToFetch = ref<number>(60);

const payments = ref<CheckLedger[]>([]);
const entities = ref<ProjectEntity[]>([]);

const propsStore = usePropsStore();
const darkVal = ref(propsStore.darkMode);

const showImportInterest = ref(false);
const interestImportFile = ref();
const interestImportHasErrors = ref(false);
const interestImported = ref(false);
const interestImportResults = ref<InterestReserveImport[]>([]);
const interestLinesSkipped = ref(0);

const showProjectReconcile = ref(false);
const projectReconcileFile = ref();
const projectReconcileResults = ref<ProjectReconcileResponse>();

const launching = ref(false);

const params = computed<Map<string, string>>(() => {
  let map = new Map<string, string>();

  if (props.project) {
    map.set("projectId", props.project.projectInfo.projectId);
    map.set("drawNumber", props.project.projectDrawInfo.drawNumber.toString());
  } else {
    map.set("daysToFetch", daysToFetch.value + "");
  }

  return map;
});

watch(
  () => propsStore.darkMode,
  (n: boolean) => (darkVal.value = n)
);

onBeforeMount(async (): Promise<void> => {
  await reloadPayments();

  if (props.project) {
    await axios
      .get(`/project-entity/${route.params.id}/${props.project.projectDrawInfo.drawNumber}`)
      .then((response) => (entities.value = ProjectEntityListSchema.parse(response.data.result)))
      .catch((e) => errorApi(e));
  }
});

const getRowId = (params: GetRowIdParams): string => {
  return params.data.ledgerId;
};

const reloadPayments = async (): Promise<void> => {
  if (params.value.get("daysToFetch") == "null") return;
  await axios
    .get(buildUrl(`/ledger/${ledger.value.checkingAccountId}/items`, params.value))
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          parseData(CheckLedgerListSchema, data, payments);
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const openImportInterestModal = (): void => {
  interestImported.value = false;
  interestImportResults.value = [];
  showImportInterest.value = true;
};

const importInterest = (): void => {
  if (interestImportFile.value == null) {
    error("No Import File Selected", "");
    return;
  }

  const data = new FormData();
  data.append("importFile", interestImportFile.value);

  axios
    .post(`/ledger/${ledger.value.checkingAccountId}/import-interest`, data, { headers: { "Content-Type": "multipart/form-data" } })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          const response = InterestReserveImportResponseSchema.parse(data);
          interestImportHasErrors.value = response.hasErrors;
          interestLinesSkipped.value = response.linesSkipped;
          interestImportResults.value = response.interestReserveImportResults;
          interestImported.value = true;
          reloadPayments();
        },
        (errorlist: ApiError[]) => {
          for (let e of errorlist) {
            error("Import Error", e.message, 10000);
          }
        }
      );
    })
    .catch((e) => errorApi(e));
};

const downloadImportReport = (): void => {
  axios
    .post("/ledger/import-interest/report", interestImportResults.value)
    .then((response) => {
      handleApiResponse(
        response,
        (fileName: any) => {
          success("Opening Interest Reserve Import Report...", " ");
          fileSaveAs("/reports/download/" + fileName, fileName);
        },
        (errorlist: ApiError[]) => {
          for (let e of errorlist) {
            error("Import Error", e.message, 10000);
          }
        }
      );
    })
    .catch((e) => errorApi(e));
};

const openReconcileModal = (): void => {
  projectReconcileResults.value = undefined;
  showProjectReconcile.value = true;
};

const reconcileProjects = (): void => {
  if (projectReconcileFile.value == null) {
    error("No Reconcile File Selected", "");
    return;
  }

  const data = new FormData();
  data.append("reconcileFile", projectReconcileFile.value);

  axios
    .post(`/ledger/${ledger.value.checkingAccountId}/reconcile`, data, { headers: { "Content-Type": "multipart/form-data" } })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          parseData(ProjectReconcileResponseSchema, data, projectReconcileResults);
        },
        (errorlist: ApiError[]) => {
          for (let e of errorlist) {
            error("Reconcile Error", e.message, 10000);
          }
        }
      );
    })
    .catch((e) => errorApi(e));
};

const downloadProjectReconcileReport = (): void => {
  if (projectReconcileResults.value == null) return;

  let url = "/reports/launch/ProjectReconciliationReport";
  let data: ReportOptions = {
    startDate: projectReconcileResults.value.reconcileDate,
    endDate: projectReconcileResults.value.reconcileDate,
  };
  axios.post(url, data).then((response) => {
    handleApiResponse(
      response,
      (fileName: string) => {
        if (fileName == null || fileName.length == 0) {
          success("There were no results found when running Project ReconcileReport.", "");
        } else {
          success("Opening Project Reconciliation Report...", "");
          fileSaveAs("/reports/download/" + fileName, fileName);
        }
        launching.value = false;
      },
      (errors: ApiError[]) => {
        if (errors.length > 0) error("Error Occurred", errors[0].message);
        launching.value = false;
      }
    );
  });
  launching.value = true;
};

const exportLedger = (): void => {
  axios
    .get(`/ledger/${ledger.value.checkingAccountId}/export`)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Payment export file has been generated");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const isRowSelectable = (node: any): boolean => {
  return node.data && !node.data.voided && !node.data.stopPayments;
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const rowClassRules = ref({
  "bg-danger-500/40!": (params: RowClassParams) => {
    return params.data.stopPayments;
  },
});

const onCellClicked = (event: CellClickedEvent): void => {
  const colDef = event.colDef;
  if (!colDef.editable) {
    event.node.setSelected(!event.node.isSelected());
    event.api.clearFocusedCell();
  }
};

const updateNotes = (event: CellEditingStoppedEvent): void => {
  if (!event.valueChanged) return;

  const data = new FormData();
  data.append("notes", event.data.notes);
  axios
    .post("/ledger/edit-notes/" + event.data.ledgerId, data)
    .then((response) => {
      handleApiResponse(
        response,
        (result: any) => {},
        (errors: ApiError[]) => {
          error("An Error Occurred", errors[0].message);
          const row = gridApi.value?.getRowNode(event.data.ledgerId);
          if (row == null) return;
          row.setDataValue(event.column.getColId(), event.oldValue);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const updateSelected = (event: SelectionChangedEvent): void => {
  toProcessCount.value = 0;
  toVoidCount.value = 0;

  for (let payment of event.api.getSelectedRows()) {
    if (payment.paid) {
      toVoidCount.value++;
    } else {
      toProcessCount.value++;
    }
  }
};

const askProcessPayments = (): void => {
  if (!props.permissions.processSelectedTasksDraw) {
    permissionError();
    return;
  }

  const selectedRows = gridApi.value?.getSelectedRows();

  if (selectedRows == null || selectedRows.length === 0) {
    info("No Payments Selected", "Select payments in the table below to process them");
    return;
  }

  confirmPopup(
    confirm,
    "Process selected payments?",
    "Unprocessed items will be marked paid while payments that have already been processed will be voided.  Are you sure that you want to process the selected payments?",
    processPayments,
    () => info("Action cancelled", "Payments not processed")
  );
};

const processPayments = (): void => {
  const selectedRows = gridApi.value?.getSelectedRows();

  if (selectedRows == null || selectedRows.length === 0) return;

  let data = new FormData();

  for (let i = 0; i < selectedRows.length; i++) {
    data.append("items[]", selectedRows[i].ledgerId);
  }

  axios
    .post("/ledger/process", data)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", toProcessCount.value + " payments processed and " + toVoidCount.value + " payments voided");
          reloadPayments();
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => {});
};

const selectAllUnprocessedRows = (): void => {
  const selected: any = [];
  const deselected: any = [];

  gridApi.value?.forEachNode(function (node: any) {
    if ((!node.data.paid && !node.data.voided) || node.isSelected()) {
      selected.push(node);
    } else {
      deselected.push(node);
    }
  });
  gridApi.value?.setNodesSelected({ nodes: selected, newValue: true });
  gridApi.value?.setNodesSelected({ nodes: deselected, newValue: false });
};

const clearSelectedRows = (): void => {
  gridApi.value?.deselectAll();
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const columns = ref([
  {
    field: "stopPayments",
    headerName: "",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "flex items-center justify-center",
    maxWidth: 50,
    cellRenderer: ErrorIconCellRenderer,
    cellRendererParams: (p: any) => {
      return {
        error: p.data.stopPaymentsReason,
      };
    },
  },
  {
    headerName: "Status",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
    valueGetter: (params: ValueGetterParams) => (params.data.voided ? "Void" : params.data.paid ? "Paid" : "Unprocessed"),
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.PAYMENT_STATUS,
    },
  },
  {
    field: "projectName",
    headerName: "Project",
    width: 100,
    hide: props.project != null,
  },
  {
    field: "drawNumber",
    headerName: "Draw #",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
  },
  {
    field: "checkAction",
    headerName: "Type",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
  },
  {
    field: "checkNumber",
    headerName: "Check #",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
  },
  {
    field: "checkDate",
    headerName: "Dated",
    cellDataType: "date",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 100,
  },
  {
    field: "payee",
    headerName: "Payee",
    width: 150,
  },
  {
    field: "loanAmount",
    headerName: "Loan Amount",
    cellDataType: "text",
    headerClass: "grid-align-center",
    cellClass: "text-right",
    width: 100,
    valueFormatter: (params: ValueFormatterParams) => formatCurrency(params.data.loanAmount),
  },
  {
    field: "equityAmount",
    headerName: "Equity Amount",
    cellDataType: "text",
    headerClass: "grid-align-center",
    cellClass: "text-right",
    valueFormatter: (params: ValueFormatterParams) => formatCurrency(params.data.equityAmount),
    width: 100,
  },
  {
    field: "notes",
    headerName: "Notes",
    cellDataType: "text",
    cellEditorParams: { maxLength: 200 },
    editable: true,
  },
]);

defineExpose({ reloadPayments });
</script>
